import {
  getFontCss,
  styleConstants,
  Tag,
  TagColorSchemes,
  TagSizes,
  themeConstants,
} from '@paywithextend/components'
import _ from 'lodash'
import React from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { CreateNewDropdownOption } from 'src/components/CreateNewDropdown/CreateNewDropdown'
import SvgCheckFilled from 'src/components/Svgs/SvgCheckFilled'
import TestId from 'src/components/TestId/TestId'
import Topnav, {
  TopnavAlternativeActionOptions,
} from 'src/components/Topnav/Topnav'
import UserAvatar from 'src/components/UserAvatar/UserAvatar'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { AvatarSizes, OrganizationRoles } from 'src/enums'
import { useBackgroundDispatch } from 'src/hooks'
import { TranslateFn } from 'src/localization/translate'
import { useTranslate } from 'src/localization/Trns'
import { UserListModel } from 'src/models/user.models'
import { VirtualCardSecureModel } from 'src/models/virtualCard.models'
import { VirtualCardPermissionsModel } from 'src/models/virtualCardPermissions.models'
import {
  closeBillPayCardUrl,
  closeVirtualCardUrl,
  deactivateBillPayCardUrl,
  deactivateVirtualCardUrl,
  shareBillPayVirtualCardUrl,
  updateBillPayUrl,
  updateVirtualCardUrl,
} from 'src/navigation/urls'
import { useQueryOrganizationFeatureFlags } from 'src/queries/organizations/featureflags.queries'
import { resendVirtualCardEmail } from 'src/redux/actions/data/virtualCards.actions'
import {
  selectUserIdFromState,
} from 'src/redux/selectors/auth.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { toDollars } from 'src/utils/currency.utils'
import styled, { css } from 'styled-components'

interface VirtualCardDetailTopnavProps {
  virtualCard: VirtualCardSecureModel
  virtualCardPermissions?: VirtualCardPermissionsModel
  recipient: UserListModel | undefined
  recipientEmail: string | undefined
}

const ShareBillPayVirtualCardDropdownOption = (
  translate: TranslateFn,
  virtualCardId: string,
): CreateNewDropdownOption => {
  return {
    label: translate('Share with Vendor'),
    subtitle: (
      <p
        css={css`
          pointer-events: none;
          ${getFontCss(
            12,
            styleConstants.colors.gray_darkest,
            styleConstants.fontFamilies.regular,
          )}
        `}
      >
        {translate('Securely send the virtual card details via email')}
      </p>
    ),
    to: shareBillPayVirtualCardUrl(virtualCardId),
    ariaLabelSource: 'Share with Vendor',
  }
}

const UpdateVirtualCardDropdownOption = (
  translate: TranslateFn,
  virtualCardId: string,
  billPay: boolean,
): CreateNewDropdownOption => {
  return {
    label: (
      <TestId testId={'updateVirtualCard'}>
        {translate('Update Virtual Card')}
      </TestId>
    ),
    to: billPay
      ? updateBillPayUrl(virtualCardId)
      : updateVirtualCardUrl(virtualCardId),
    ariaLabelSource: 'Update Virtual Card',
  }
}

const RequestUpdateDropdownOption = (
  translate: TranslateFn,
  virtualCardId: string,
  billPay: boolean,
): CreateNewDropdownOption => {
  return {
    label: translate('Request Update'),
    to: billPay
      ? updateBillPayUrl(virtualCardId)
      : updateVirtualCardUrl(virtualCardId),
    ariaLabelSource: 'Request Update',
  }
}

const CancelRequestDropdownOption = (
  translate: TranslateFn,
  virtualCardId: string,
): CreateNewDropdownOption => {
  return {
    label: translate('Cancel Request'),
    to: deactivateVirtualCardUrl(virtualCardId),
    ariaLabelSource: 'Cancel Request',
  }
}

const ResendVirtualCardDropdownOption = (
  translate: TranslateFn,
  isGuestCard: boolean,
  billPay: boolean,
  onClick: () => void,
): CreateNewDropdownOption => {
  return {
    label: (
      <TestId testId={'resendVirtualCard'}>
        {translate('Resend Virtual Card')}
      </TestId>
    ),
    subtitle: (
      <p
        css={css`
          pointer-events: none;
          ${getFontCss(
            12,
            styleConstants.colors.gray_darkest,
            styleConstants.fontFamilies.regular,
          )}
        `}
      >
        {isGuestCard
          ? translate(
              'Send the recipient an email with a secure link to the virtual card',
            )
          : billPay
            ? translate(
                'Send the vendor an email with the virtual card details',
              )
            : translate(
                'Send the recipient an email with the virtual card details',
              )}
      </p>
    ),
    onClick,
    ariaLabelSource: 'Resend Virtual Card',
  }
}

const DeactivateVirtualCardDropdownOption = (
  translate: TranslateFn,
  virtualCardId: string,
  billPay: boolean,
): CreateNewDropdownOption => {
  return {
    label: (
      <TestId testId={'deactivateVirtualCard'}>
        {translate('Deactivate Virtual Card')}
      </TestId>
    ),
    to: billPay
      ? deactivateBillPayCardUrl(virtualCardId)
      : deactivateVirtualCardUrl(virtualCardId),
    ariaLabelSource: 'Deactivate Virtual Card',
  }
}

const CloseVirtualCardDropdownOption = (
  translate: TranslateFn,
  virtualCardId: string,
  billPay: boolean,
): CreateNewDropdownOption => {
  return {
    id: 'closeVC',
    label: translate('Close Virtual Card'),
    to: billPay
      ? closeBillPayCardUrl(virtualCardId)
      : closeVirtualCardUrl(virtualCardId),
    ariaLabelSource: 'Close Virtual Card',
    subtitle: (
      <p
        css={css`
          pointer-events: none;
          ${getFontCss(
            12,
            styleConstants.colors.gray_darkest,
            styleConstants.fontFamilies.regular,
          )}
        `}
      >
        {translate('Permanently deactivate this virtual card')}
      </p>
    ),
  }
}

const TitleDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`
const SharedTag = (): React.ReactNode => {
  const translate = useTranslate()
  return (
    <Tag
      text={translate('Shared to Vendor')}
      size={TagSizes.Medium}
      color={TagColorSchemes.Green}
      icon={
        <SvgCheckFilled
          fill={styleConstants.colors.green_darkest}
          style={{ marginLeft: '4px', marginTop: '-1px', scale: '0.9' }}
        />
      }
    />
  )
}

const TitleContent: React.FC<{
  myCard: boolean
  guestCard: boolean
  exactPay: boolean
  billPayShared: boolean
  recipient: UserListModel | undefined
}> = (props): React.ReactNode => {
  const translate = useTranslate()

  const Label = (): React.ReactNode => {
    const translate = useTranslate()

    if (props.myCard) {
      return translate(
        props.guestCard ? 'Your Guest Virtual Card' : 'Your Virtual Card',
      )
    } else if (props.recipient) {
      return translate(
        props.guestCard
          ? "${recipientFirstName}'s Guest Virtual Card"
          : "${recipientFirstName}'s Virtual Card",
        {
          recipientFirstName: props.recipient.firstNameOrEmail,
        },
      )
    } else {
      return translate(props.guestCard ? 'Guest Virtual Card' : 'Virtual Card')
    }
  }
  return (
    <TitleDiv>
      {!props.myCard && props.recipient && (
        <UserAvatar size={AvatarSizes.Small} user={props.recipient} />
      )}
      <Label />
      {props.exactPay && (
        <div
          css={css`
            margin-top: 8px;
          `}
        >
          <Tag text={translate('Exact Pay')} size={TagSizes.Small} />
        </div>
      )}
      {props.billPayShared && (
        <TestId testId={'sharedToVendorTag'}>
          <SharedTag />
        </TestId>
      )}
    </TitleDiv>
  )
}

const VirtualCardDetailTopnav: React.FC<VirtualCardDetailTopnavProps> = ({
  recipient,
  recipientEmail,
  virtualCard,
  virtualCardPermissions,
}): React.ReactNode => {
  const translate = useTranslate()

  const userId = useSelector(selectUserIdFromState)
  const user = useCurrentUser()
  const { data: orgFeatureFlags } = useQueryOrganizationFeatureFlags({
    orgId: user?.organizationId,
  })
  const showGuestRole = orgFeatureFlags?.guestRoleEnabled
  const isGuestUser =
    showGuestRole &&
    userId === virtualCard.recipientId &&
    recipient?.organizationRole === OrganizationRoles.guest
  const showBillPayShareLater = useSelector(
    selectFeatureFlaggerFromState,
  ).show_bill_pay_share_later

  const recipientIdForHeader = virtualCard.isBillPay
    ? virtualCard.cardholderId
    : virtualCard.recipientId

  const { analytics } = useAnalyticsContext()

  const dispatch = useBackgroundDispatch()

  const canDeactivate =
    virtualCardPermissions?.canUpdateVirtualCard ||
    virtualCardPermissions?.canRequestUpdate

  const isGuestCard = _.get(virtualCard, 'direct', false)
  const isBillpayCard = _.get(virtualCard, 'isBillPay', false)

  let negativeOptions: CreateNewDropdownOption[] =
    !virtualCard.isInactive && canDeactivate
      ? [
          DeactivateVirtualCardDropdownOption(
            translate,
            virtualCard.id,
            virtualCard.isBillPay,
          ),
        ]
      : []

  if (
    (virtualCard.isActive || virtualCard.isInactive) &&
    !virtualCard.isClosed &&
    virtualCardPermissions?.canUpdateVirtualCard
  ) {
    negativeOptions = [
      ...negativeOptions,
      CloseVirtualCardDropdownOption(
        translate,
        virtualCard.id,
        virtualCard.isBillPay,
      ),
    ]
  }

  const getAlternativeActionOptions = (): TopnavAlternativeActionOptions[] => {
    if (
      (!virtualCardPermissions?.canUpdateVirtualCard &&
        virtualCard.recipientId !== userId) ||
      isGuestUser
    ) {
      return []
    }

    const positiveActionOptions: CreateNewDropdownOption[] = []
    const negativeActionOptions: CreateNewDropdownOption[] = negativeOptions

    if (
      showBillPayShareLater &&
      userId &&
      virtualCard.isBillPayShareable(userId)
    ) {
      positiveActionOptions.push(
        ShareBillPayVirtualCardDropdownOption(translate, virtualCard.id),
      )
    }

    if (virtualCard.isPending) {
      negativeActionOptions.push(
        CancelRequestDropdownOption(translate, virtualCard.id),
      )
    }

    if (
      !virtualCard.isInactive &&
      virtualCardPermissions?.canUpdateVirtualCard &&
      virtualCard.recipientId !== userId &&
      recipientEmail
    ) {
      positiveActionOptions.push(
        ResendVirtualCardDropdownOption(
          translate,
          isGuestCard,
          isBillpayCard,
          (): void => {
            dispatch(resendVirtualCardEmail(virtualCard.id, recipientEmail))
            analytics?.track('resend virtual card', {
              type_of_card: virtualCard.cardTypeLabel.props.source,
              card_name: virtualCard.displayName,
              credit_limit: toDollars(virtualCard.balanceCents),
              active_until:
                virtualCard.activeUntilTimezonedMoment.format('YYYY-MM-DD'),
              add_attachments: virtualCard.receiptAttachments?.length !== 0,
              add_notes: virtualCard.notes !== undefined,
            })
          },
        ),
      )
    }

    if (
      positiveActionOptions.length === 0 &&
      negativeActionOptions.length === 0
    ) {
      return []
    }
    return [
      {
        title: (
          <TestId testId={'virtualCardsOptions'}>
            {' '}
            {translate('Virtual Card Options')}{' '}
          </TestId>
        ),
        positiveActionOptions,
        negativeActionOptions,
      },
    ]
  }

  return (
    <Topnav
      mainActionOptions={
        virtualCard.isConsumed || virtualCard.isClosed || isGuestUser
          ? undefined
          : virtualCardPermissions?.canUpdateVirtualCard
            ? {
                topActionOptions: [
                  UpdateVirtualCardDropdownOption(
                    translate,
                    virtualCard.id,
                    virtualCard.isBillPay,
                  ),
                ],
              }
            : virtualCardPermissions?.canRequestUpdate
              ? {
                  topActionOptions: [
                    RequestUpdateDropdownOption(
                      translate,
                      virtualCard.id,
                      virtualCard.isBillPay,
                    ),
                  ],
                }
              : undefined
      }
      alternativeActionOptions={getAlternativeActionOptions()}
      titleElements={{
        title: (
          <TitleContent
            myCard={userId === recipientIdForHeader}
            guestCard={isGuestCard}
            exactPay={virtualCard.features?.singleExactPay ?? false}
            billPayShared={
              virtualCard.isBillPay && virtualCard.billPayMeta?.shared === true
            }
            recipient={recipient}
          />
        ),
        scrollContent: (
          <span
            css={css`
              ${!!virtualCard.pseudoStatusModifiers &&
              `${getFontCss(16, themeConstants.colors.highlight, styleConstants.fontFamilies.regular)};`}
              ${recipientIdForHeader !== userId && `margin-left: 32px;`}
            `}
          >
            {virtualCard.isInactive
              ? virtualCard.psuedoStatusLabel
              : virtualCard.balanceLabel}
          </span>
        ),
        canRequestVirtualCardUpdateForDetailTopnav:
          virtualCardPermissions?.canRequestUpdate,
      }}
    />
  )
}

export default VirtualCardDetailTopnav
